import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

function AnimatedSection({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setVisible(true); }, { threshold: 0.06 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} style={{ opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(32px)', transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s` }}>
      {children}
    </div>
  );
}

const ONE_TIME = [
  {
    name: 'Start',
    price: 2900,
    tagline: 'Szybki start online',
    color: '#00ff88',
    highlight: false,
    badge: null,
    desc: 'Idealne dla firm, które potrzebują profesjonalnej obecności online bez dużego budżetu startowego. Solidna podstawa cyfrowa.',
    features: [
      'Landing page lub strona wizytówkowa (do 5 podstron)',
      'Responsywny design mobile-first',
      'Formularz kontaktowy + Google Maps',
      'Optymalizacja SEO On-Page (podstawowa)',
      'Google Analytics 4 + Search Console',
      'Google Business Profile (wizytówka)',
      'Certyfikat SSL + zabezpieczenia',
      'Czas realizacji: 2–3 tygodnie',
      '30 dni wsparcia po starcie',
    ],
    addons: [],
    notIncl: ['Agent AI', 'Zaawansowane SEO', 'Blog / CMS', 'Galeria 360°'],
  },
  {
    name: 'Premium',
    price: 7900,
    tagline: 'Strona, która sprzedaje',
    color: '#00d4ff',
    highlight: true,
    badge: 'Najpopularniejszy',
    desc: 'Pełna strona premium z kompleksową strategią SEO i systemem rezerwacji. Dla firm nastawionych na wzrost organiczny i konwersję.',
    features: [
      'Strona premium (do 12 podstron + blog/CMS)',
      'Design System + produktowy UX/UI',
      'System rezerwacji / kalendarza online',
      'Kompleksowe SEO techniczne i treściowe',
      'Strategia słów kluczowych (50+ fraz)',
      'Core Web Vitals 90+ (Lighthouse)',
      'Integracje: newsletter, CRM, rezerwacje',
      'Google Analytics 4 + heatmapy',
      'Galeria zdjęć / wideo premium',
      'Czas realizacji: 4–6 tygodni',
      '60 dni opieki po starcie',
    ],
    addons: [],
    notIncl: ['Agent AI (dostępny jako add-on +3500 zł)'],
  },
  {
    name: 'AI Pro',
    price: 14900,
    tagline: 'Pełna transformacja cyfrowa',
    color: '#ff6b9d',
    highlight: false,
    badge: 'Polecamy dla turystyki',
    desc: 'Strona premium + w pełni skonfigurowany agent AI + kompleksowe SEO i budowanie reputacji. Kompletna transformacja cyfrowa.',
    features: [
      'Wszystko z pakietu Premium',
      'Agent AI (chatbot + voice) dedykowany firmie',
      'Automatyzacja rezerwacji, FAQ i zapytań',
      'Integracja AI z CRM i e-mail marketingiem',
      'Kampania Google Ads (konfiguracja + start)',
      'Lokalne SEO — dominacja wyników map',
      'Kampania reputacji Google (opinie 5★)',
      'Wirtualny spacer 360° (1 obiekt)',
      'Miesięczny raport AI + wyników SEO',
      'Czas realizacji: 6–9 tygodni',
      '90 dni opieki + priorytetowy support',
    ],
    addons: [],
    notIncl: [],
  },
  {
    name: 'Enterprise',
    price: 0,
    tagline: 'Systemy klasy enterprise',
    color: '#00d4ff',
    highlight: false,
    badge: null,
    desc: 'Dedykowane oprogramowanie AI, systemy EUDR, sieć agentów AI i zaawansowane integracje dla firm wymagających skalowalnych rozwiązań.',
    features: [
      'Dedykowane oprogramowanie AI na miarę',
      'Systemy zgodności EUDR z modułem AI',
      'Sieć powiązanych agentów AI (multi-agent)',
      'Integracje REST API z ERP / CRM / WMS',
      'Dashboard analityczny w czasie rzeczywistym',
      'Szkolenia dla zespołu (warsztaty + materiały)',
      'SLA z gwarantowanym czasem odpowiedzi 4h',
      'Dedykowany Project Manager',
      'Raporty efektywności AI i ROI',
      'Audyt bezpieczeństwa i testy penetracyjne',
      'Nieograniczona liczba modułów i podstron',
      'Wsparcie wdrożeniowe do 12 miesięcy',
    ],
    addons: [],
    notIncl: [],
  },
];

const MONTHLY = [
  {
    name: 'SEO Basic',
    price: 800,
    color: '#00ff88',
    desc: 'Podstawowe pozycjonowanie dla firm lokalnych.',
    features: [
      'Audyt i optymalizacja 5 podstron / mies.',
      'Monitoring pozycji 50 fraz kluczowych',
      'Aktualizacje techniczne SEO',
      '1 artykuł blogowy optymalizowany SEO',
      'Optymalizacja wizytówki Google',
      'Raport miesięczny z pozycji',
    ],
  },
  {
    name: 'SEO Premium',
    price: 1900,
    color: '#00d4ff',
    desc: 'Intensywne pozycjonowanie i content marketing.',
    features: [
      'Optymalizacja 15 podstron miesięcznie',
      'Monitoring 150 fraz kluczowych',
      'Link Building (5 linków wysokiej jakości / mies.)',
      '4 artykuły blogowe / mies.',
      'Kampania Google Ads — opcjonalnie',
      'Raport cotygodniowy + call strategiczny',
    ],
  },
  {
    name: 'Agent AI Lite',
    price: 500,
    color: '#ff6b9d',
    desc: 'Utrzymanie i rozwój agenta AI.',
    features: [
      'Hosting i infrastruktura agenta AI',
      'Aktualizacja bazy wiedzy (1× / mies.)',
      'Raport rozmów, konwersji i intencji',
      'Do 500 rozmów AI miesięcznie',
      'Monitoring dostępności 24/7',
      'Wsparcie email (czas odpowiedzi 24h)',
    ],
  },
  {
    name: 'Agent AI Pro',
    price: 1200,
    color: '#00d4ff',
    desc: 'Ciągłe uczenie i nieograniczone rozmowy.',
    features: [
      'Hosting + uczenie agenta na nowych danych',
      'Aktualizacje bazy wiedzy (4× / mies.)',
      'Nieograniczona liczba rozmów AI',
      'Integracje z CRM / booking / e-mail',
      'Analityka konwersacji + optymalizacja',
      'Wsparcie priorytetowe (czas odpowiedzi 4h)',
    ],
  },
];

const COMPARE_ROWS = [
  { label: 'Liczba podstron', start: 'do 5', premium: 'do 12 + blog', aipro: 'Bez limitu', enterprise: 'Bez limitu' },
  { label: 'Design System', start: '—', premium: '+', aipro: '+', enterprise: '+' },
  { label: 'Responsywność mobile', start: '+', premium: '+', aipro: '+', enterprise: '+' },
  { label: 'SEO On-Page', start: 'Podstawowe', premium: 'Kompleksowe', aipro: 'Kompleksowe', enterprise: 'Kompleksowe' },
  { label: 'System rezerwacji', start: '—', premium: '+', aipro: '+', enterprise: '+' },
  { label: 'Agent AI', start: '—', premium: 'Add-on', aipro: '+', enterprise: '+' },
  { label: 'Galeria 360°', start: '—', premium: '—', aipro: '1 obiekt', enterprise: 'Wiele obiektów' },
  { label: 'Google Ads (start)', start: '—', premium: '—', aipro: '+', enterprise: '+' },
  { label: 'Kampania reputacji', start: '—', premium: '—', aipro: '+', enterprise: '+' },
  { label: 'Czas opieki po starcie', start: '30 dni', premium: '60 dni', aipro: '90 dni', enterprise: 'do 12 mies.' },
  { label: 'Czas realizacji', start: '2–3 tygodnie', premium: '4–6 tygodni', aipro: '6–9 tygodni', enterprise: 'Wycena indyw.' },
];

const FAQS = [
  {
    q: 'Czym różni się pakiet Premium od AI Pro?',
    a: 'Pakiet Premium to pełna strona z kompleksowym SEO i systemem rezerwacji — idealna baza dla każdej firmy. AI Pro dodaje do tego w pełni skonfigurowanego agenta AI (chatbot + voice), kampanię Google Ads, budowanie reputacji i wirtualny spacer 360°. To kompletna transformacja cyfrowa, nie tylko strona.',
  },
  {
    q: 'Co zawiera "opieka po starcie" wliczona w pakiet?',
    a: 'Opieka po starcie obejmuje: poprawki projektowe do 5 godzin roboczych, aktualizacje techniczne i bezpieczeństwa, monitoring dostępności i wydajności strony oraz priorytetowe wsparcie mailowe. Po tym czasie przechodzimy na jeden z pakietów miesięcznych lub rozliczamy zmiany godzinowo.',
  },
  {
    q: 'Czy mogę dodać agenta AI do istniejącej strony?',
    a: 'Tak. Projektujemy wszystkie strony z myślą o przyszłej rozbudowie. Dodanie agenta AI do istniejącej strony zaczyna się od 3 500 zł, w zależności od zakresu integracji i złożoności bazy wiedzy agenta.',
  },
  {
    q: 'Jak wygląda wycena pakietu Enterprise?',
    a: 'Wycena opiera się na analizie zakresu — liczbie modułów AI, potrzebnych integracjach, złożoności algorytmów, wymaganym SLA i liczbie godzin deweloperskich. Po bezpłatnej konsultacji przygotowujemy szczegółową ofertę w ciągu 3–5 dni roboczych.',
  },
  {
    q: 'Czy pakiety miesięczne mają umowę na czas określony?',
    a: 'Standardowa umowa SEO to 6 miesięcy — minimalne zaangażowanie, żeby zobaczyć realne wyniki pozycjonowania. Po tym czasie możesz kontynuować lub zakończyć z miesięcznym wypowiedzeniem. Pakiety agentów AI mogą być rozliczane miesiąc do miesiąca od początku współpracy.',
  },
  {
    q: 'Czy oferujecie możliwość płatności w ratach?',
    a: 'Tak. Dla pakietów Premium i AI Pro oferujemy możliwość rozłożenia płatności: 50% zaliczki przed startem projektu i 50% po dostarczeniu gotowego produktu. Dla projektów Enterprise dostępne są indywidualne harmonogramy płatności etapowych.',
  },
];

export default function PakietyPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [showCompare, setShowCompare] = useState(false);

  return (
    <div className="min-h-screen bg-[#080808] text-white overflow-x-hidden">
      <Navbar />

      {/* Hero */}
      <section className="pt-40 pb-24 px-4 md:px-6 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(0,212,255,0.08) 0%, transparent 60%)' }}
        />
        <div className="max-w-[1100px] mx-auto text-center relative z-10">
          <AnimatedSection>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#00d4ff]/30 bg-[#00d4ff]/5 backdrop-blur-sm mb-8">
              <span className="w-2 h-2 rounded-full bg-[#00d4ff] animate-pulse" />
              <span className="text-[#00d4ff] text-xs font-semibold tracking-widest uppercase">Cennik & Pakiety</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-black leading-tight mb-8">
              Transparentne ceny,<br />
              <span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(135deg, #00d4ff, #00ff88)' }}>
                realne wyniki
              </span>
            </h1>
            <p className="text-xl text-white/45 max-w-3xl mx-auto leading-relaxed">
              Żadnych ukrytych kosztów, żadnych niespodzianek. Jasne pakiety z dokładnym zakresem prac, terminami realizacji i gwarantowanymi deliverables.
            </p>
            <div className="flex flex-wrap gap-8 justify-center mt-10">
              {[
                { icon: 'ri-money-cny-circle-line', text: 'Możliwość płatności w ratach' },
                { icon: 'ri-shield-check-line', text: 'Gwarancja terminu realizacji' },
                { icon: 'ri-customer-service-2-line', text: 'Stały opiekun projektu' },
              ].map((b) => (
                <div key={b.text} className="flex items-center gap-2 text-white/40 text-sm">
                  <i className={`${b.icon} text-[#00d4ff]`} />
                  {b.text}
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* One-time packages */}
      <section className="pb-24 px-4 md:px-6">
        <div className="max-w-[1400px] mx-auto">
          <AnimatedSection>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-5 mb-12">
              <div>
                <p className="text-white/40 text-xs uppercase tracking-[2px] font-semibold mb-2">Projekty jednorazowe</p>
                <h2 className="text-3xl md:text-4xl font-black text-white">Wdrożenia i realizacje</h2>
              </div>
              <button
                onClick={() => setShowCompare(!showCompare)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-white/15 text-white/50 hover:text-white hover:border-white/30 text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-table-line" />
                {showCompare ? 'Ukryj' : 'Pokaż'} porównanie szczegółowe
              </button>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
            {ONE_TIME.map((pkg, idx) => (
              <AnimatedSection key={pkg.name} delay={idx * 0.08}>
                <div
                  className="relative rounded-3xl border h-full flex flex-col overflow-hidden transition-all duration-300 hover:-translate-y-1"
                  style={{
                    background: pkg.highlight ? `${pkg.color}06` : 'rgba(255,255,255,0.02)',
                    borderColor: pkg.highlight ? `${pkg.color}45` : 'rgba(255,255,255,0.08)',
                  }}
                >
                  {pkg.badge && (
                    <div className="absolute top-0 left-0 right-0 py-2 text-center text-[10px] font-bold tracking-widest uppercase"
                      style={{ background: `${pkg.color}22`, color: pkg.color }}>
                      {pkg.badge}
                    </div>
                  )}
                  <div className={`p-7 flex flex-col flex-1 ${pkg.badge ? 'pt-10' : ''}`}>
                    <div className="text-[10px] uppercase tracking-[2px] font-bold mb-2" style={{ color: pkg.color }}>{pkg.name}</div>
                    <p className="text-white/50 text-xs font-medium mb-4 italic">{pkg.tagline}</p>
                    <div className="flex items-end gap-2 mb-3">
                      {pkg.price > 0 ? (
                        <>
                          <span className="text-4xl font-black text-white">{pkg.price.toLocaleString('pl')} zł</span>
                          <span className="text-white/30 text-xs mb-1">jednorazowo</span>
                        </>
                      ) : (
                        <span className="text-4xl font-black text-white">Wycena</span>
                      )}
                    </div>
                    <p className="text-white/40 text-xs leading-relaxed mb-6">{pkg.desc}</p>

                    <ul className="space-y-2.5 mb-6 flex-1">
                      {pkg.features.map((f) => (
                        <li key={f} className="flex items-start gap-2.5">
                          <i className="ri-checkbox-circle-fill text-xs mt-0.5 flex-shrink-0" style={{ color: pkg.color }} />
                          <span className="text-white/50 text-xs leading-relaxed">{f}</span>
                        </li>
                      ))}
                      {pkg.notIncl.map((f) => (
                        <li key={f} className="flex items-start gap-2.5">
                          <i className="ri-close-circle-line text-xs mt-0.5 flex-shrink-0 text-white/20" />
                          <span className="text-white/25 text-xs leading-relaxed">{f}</span>
                        </li>
                      ))}
                    </ul>

                    <Link
                      to="/kontakt"
                      className="block text-center py-3.5 rounded-xl font-bold text-sm whitespace-nowrap cursor-pointer hover:scale-105 transition-transform"
                      style={{
                        background: pkg.highlight ? `linear-gradient(135deg, ${pkg.color}, #00b8e6)` : `${pkg.color}14`,
                        color: pkg.highlight ? '#000' : pkg.color,
                        border: pkg.highlight ? 'none' : `1px solid ${pkg.color}30`,
                      }}
                    >
                      {pkg.price === 0 ? 'Zapytaj o wycenę' : 'Wybierz ten pakiet'}
                    </Link>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>

          {/* Comparison table */}
          {showCompare && (
            <div className="mt-12 overflow-x-auto">
              <AnimatedSection>
                <table className="w-full min-w-[700px]" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                  <thead>
                    <tr>
                      <th className="text-left text-white/30 text-xs font-semibold uppercase tracking-wider py-4 px-5 border-b border-white/8">Funkcja</th>
                      {['Start', 'Premium', 'AI Pro', 'Enterprise'].map((n, i) => {
                        const colors = ['#00ff88','#00d4ff','#ff6b9d','#00d4ff'];
                        return (
                          <th key={n} className="text-center py-4 px-4 border-b border-white/8">
                            <span className="text-xs font-black uppercase tracking-wider" style={{ color: colors[i] }}>{n}</span>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {COMPARE_ROWS.map((row, idx) => (
                      <tr key={row.label} className={idx % 2 === 0 ? '' : ''}>
                        <td className="py-3 px-5 text-white/45 text-sm border-b border-white/5">{row.label}</td>
                        {[row.start, row.premium, row.aipro, row.enterprise].map((val, ci) => {
                          const colors = ['#00ff88','#00d4ff','#ff6b9d','#00d4ff'];
                          return (
                            <td key={ci} className="py-3 px-4 text-center text-sm border-b border-white/5">
                              {val === '+' ? (
                                <span className="text-lg" style={{ color: colors[ci] }}>✓</span>
                              ) : val === '—' ? (
                                <span className="text-white/20">—</span>
                              ) : (
                                <span className="text-white/50 text-xs">{val}</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </AnimatedSection>
            </div>
          )}
        </div>
      </section>

      {/* Monthly */}
      <section className="py-24 px-4 md:px-6 bg-[#060606] border-y border-white/5">
        <div className="max-w-[1400px] mx-auto">
          <AnimatedSection>
            <div className="text-center mb-14">
              <p className="text-white/40 text-xs uppercase tracking-[2px] font-semibold mb-3">Subskrypcje miesięczne</p>
              <h2 className="text-3xl md:text-4xl font-black text-white mb-3">Stała opieka i wzrost</h2>
              <p className="text-white/40 text-base max-w-xl mx-auto">SEO i agenci AI wymagają ciągłej pracy. Pakiety miesięczne zapewniają systematyczne, mierzalne wyniki.</p>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
            {MONTHLY.map((pkg, idx) => (
              <AnimatedSection key={pkg.name} delay={idx * 0.08}>
                <div className="p-7 rounded-3xl border border-white/8 bg-white/[0.02] hover:border-white/16 transition-all duration-300 hover:-translate-y-1 h-full flex flex-col">
                  <div className="text-[10px] uppercase tracking-[2px] font-bold mb-1" style={{ color: pkg.color }}>{pkg.name}</div>
                  <p className="text-white/35 text-xs mb-4 italic">{pkg.desc}</p>
                  <div className="flex items-end gap-1 mb-5">
                    <span className="text-4xl font-black text-white">{pkg.price.toLocaleString('pl')} zł</span>
                    <span className="text-white/30 text-xs mb-1">/mies.</span>
                  </div>
                  <ul className="space-y-2.5 flex-1 mb-7">
                    {pkg.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5">
                        <i className="ri-checkbox-circle-fill text-xs mt-0.5 flex-shrink-0" style={{ color: pkg.color }} />
                        <span className="text-white/50 text-xs leading-relaxed">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/kontakt"
                    className="block text-center py-3 rounded-xl font-bold text-sm whitespace-nowrap cursor-pointer hover:opacity-85 transition-opacity"
                    style={{ background: `${pkg.color}14`, color: pkg.color, border: `1px solid ${pkg.color}30` }}
                  >
                    Wybierz pakiet
                  </Link>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Add-ons strip */}
      <section className="py-16 px-4 md:px-6">
        <div className="max-w-[1200px] mx-auto">
          <AnimatedSection>
            <div className="text-center mb-10">
              <p className="text-[#00d4ff] text-xs uppercase tracking-[3px] font-semibold mb-3">Add-ony</p>
              <h2 className="text-3xl font-black text-white">Dodatkowe usługi</h2>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { name: 'Agent AI (do istn. strony)', price: 'od 3 500 zł', icon: 'ri-robot-2-line', color: '#00d4ff' },
              { name: 'Wirtualny spacer 360°', price: 'od 1 200 zł / obiekt', icon: 'ri-360-line', color: '#00ff88' },
              { name: 'Kampania Google Ads', price: 'od 800 zł / mies.', icon: 'ri-google-line', color: '#ff6b9d' },
              { name: 'Audyt SEO + Raport', price: 'od 700 zł', icon: 'ri-search-eye-line', color: '#00d4ff' },
            ].map((a) => (
              <AnimatedSection key={a.name} delay={0.08}>
                <div className="p-6 rounded-2xl border border-white/8 bg-white/[0.02] hover:border-white/16 transition-all flex items-center gap-4">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: `${a.color}12`, border: `1px solid ${a.color}25` }}>
                    <i className={`${a.icon} text-xl`} style={{ color: a.color }} />
                  </div>
                  <div>
                    <div className="font-bold text-white text-sm">{a.name}</div>
                    <div className="text-xs mt-0.5" style={{ color: a.color }}>{a.price}</div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 md:px-6 bg-[#060606] border-y border-white/5">
        <div className="max-w-[900px] mx-auto">
          <AnimatedSection>
            <div className="text-center mb-14">
              <p className="text-[#ff6b9d] text-xs uppercase tracking-[3px] font-semibold mb-4">FAQ</p>
              <h2 className="text-4xl font-black text-white mb-4">Często zadawane pytania</h2>
            </div>
          </AnimatedSection>
          <div className="space-y-2.5">
            {FAQS.map((faq, idx) => (
              <AnimatedSection key={idx} delay={idx * 0.04}>
                <div className="rounded-2xl border border-white/8 overflow-hidden transition-all duration-300"
                  style={{ background: openFaq === idx ? 'rgba(0,212,255,0.03)' : 'rgba(255,255,255,0.02)' }}>
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between p-6 text-left cursor-pointer"
                  >
                    <span className="text-white font-semibold text-sm md:text-base pr-6">{faq.q}</span>
                    <i className={`ri-arrow-down-s-line text-xl transition-transform duration-300 flex-shrink-0 ${openFaq === idx ? 'rotate-180' : ''}`}
                      style={{ color: openFaq === idx ? '#00d4ff' : 'rgba(255,255,255,0.3)' }} />
                  </button>
                  <div style={{ maxHeight: openFaq === idx ? '400px' : '0px', overflow: 'hidden', transition: 'max-height 0.4s ease' }}>
                    <p className="px-6 pb-6 text-white/50 leading-relaxed text-sm">{faq.a}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-4 md:px-6">
        <div className="max-w-[900px] mx-auto text-center">
          <AnimatedSection>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">Nie wiesz który pakiet wybrać?</h2>
            <p className="text-white/40 text-lg mb-10 max-w-2xl mx-auto leading-relaxed">
              Na bezpłatnej 30-minutowej konsultacji dobierzemy razem idealne rozwiązanie dla Twojego biznesu i budżetu.
            </p>
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 text-black px-10 py-5 rounded-xl font-bold text-lg whitespace-nowrap cursor-pointer hover:scale-105 transition-transform"
              style={{ background: 'linear-gradient(135deg, #00d4ff, #00b8e6)', boxShadow: '0 0 40px rgba(0,212,255,0.25)' }}
            >
              Porozmawiajmy o Twoim projekcie
              <i className="ri-arrow-right-line" />
            </Link>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </div>
  );
}
